import React, { Fragment } from 'react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { MessageCircle, Share2, Globe, Users, Lock, MoreVertical, Shield, Trash2 } from 'lucide-react'
import { FaPlayCircle, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa'
import { s3Static } from '../../../utils/constants'
import ReactPlayer from 'react-player'
import { LikeButton } from './LikeButton'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'

export const BuzzCard = ({
    buzz,
    index,
    viewEntityId,
    isMuteVideo,
    isPauseVideo,
    setIsMuteVideo,
    setIsPauseVideo,
    onViewportEnter,
    onOpenImage,
    onOpenBuzz,
    onLike,
    onUnlike,
    onShare,
    // Optional props for owner actions (MyBuzz only)
    isOwner = false,
    onEditPrivacy,
    onDelete,
}) => {

    return (
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className='w-full bg-white relative rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300'
        >
            <motion.div 
                className='p-4'
                onViewportEnter={onViewportEnter}
                viewport={{ margin: "-50% 0% -50% 0%"}}
            >   
                {/* Activity Header */}
                {(buzz.actTyp === "commented on" || buzz.actTyp === "liked") && (
                    <div className='text-xs border-b border-gray-100 pb-2 mb-3'>
                        <Link href={`/profile/${buzz.actHandleName}`} className='font-medium text-orange-600 hover:text-orange-700'>
                            {buzz.actUserNm}
                        </Link>
                        <span className='text-gray-500'>
                            &nbsp;{buzz.actTyp}
                        </span>
                    </div>                    
                )}
                
                {/* User Header */}
                <div className='flex items-start justify-between'>
                    <div className='flex items-center gap-3'>
                        <Link href={`/profile/${buzz.handleName}`} className='relative cursor-pointer shrink-0'>
                            <div className='w-11 h-11 rounded-full overflow-hidden ring-2 ring-gray-100'>
                                <Image 
                                    src={buzz.ImageProfile}
                                    height={44}
                                    width={44}
                                    style={{objectFit: 'cover'}}
                                    placeholder="blur"
                                    blurDataURL={s3Static + "placeholder.png"}
                                    alt='Profile'
                                    className='w-full h-full'
                                />
                            </div>
                        </Link>
                        <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-1.5'>
                                <Link href={`/profile/${buzz.handleName}`} className='font-semibold text-gray-900 text-sm hover:text-orange-600 transition-colors truncate'>
                                    {buzz.userFullNm}
                                </Link>
                                {(buzz.actTyp === "commented on" || buzz.actTyp === "liked") ?
                                    <span className="text-gray-500 text-xs">
                                        &apos;s {buzz.entityTyp}
                                    </span>   
                                :
                                    <span className='text-gray-500 text-xs'>
                                        {buzz.actTyp}
                                        {buzz.entityTyp === "photo" && <span> a photo</span>}
                                    </span> 
                                }
                            </div>
                            <div className='text-gray-400 text-[11px] flex items-center gap-1'>
                                <span>{buzz.displayTime}</span>
                                <span>•</span>
                                {buzz.postSecurity === 'public' && 
                                    <Globe size={12} className='text-gray-400'/>
                                }
                                {buzz.postSecurity === 'strings' && 
                                    <Users size={12} className='text-gray-400'/>
                                }
                                {buzz.postSecurity === 'private' && 
                                    <Lock size={12} className='text-gray-400'/>
                                }
                            </div>
                        </div>          
                    </div>
                    
                    {/* Owner Menu */}
                    {isOwner && (
                        <Menu as="div" className="relative">
                            <MenuButton className='p-2 hover:bg-gray-100 rounded-xl transition-colors -mr-2'>
                                <MoreVertical size={18} className='text-gray-400' />
                            </MenuButton>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-200"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-150"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <MenuItems className='absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden focus:outline-none z-10'>
                                    <MenuItem>
                                        {({ active }) => (
                                            <button 
                                                onClick={() => onEditPrivacy(buzz.entityId, buzz.postSecurity)}
                                                className={`${active ? 'bg-gray-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700`}
                                            >
                                                <Shield size={16} className='text-gray-400' />
                                                Edit Privacy
                                            </button>
                                        )}
                                    </MenuItem>
                                    <MenuItem>
                                        {({ active }) => (
                                            <button 
                                                onClick={() => onDelete(buzz.entityId)}
                                                className={`${active ? 'bg-red-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 border-t border-gray-50`}
                                            >
                                                <Trash2 size={16} />
                                                Delete Post
                                            </button>
                                        )}
                                    </MenuItem>
                                </MenuItems>
                            </Transition>
                        </Menu>
                    )}
                </div>

                {/* Description */}
                {buzz.desc && (
                    <div className='text-sm md:text-base text-gray-700 mt-3'>   
                        <span>{buzz.desc}</span>
                    </div>
                )}  

                {/* Media */}
                {buzz.media != '' && (
                    <>
                        {(buzz.mediaType.includes("image") || buzz.mediaType.includes("gif")) && (
                            /* Full image at natural aspect ratio, capped at 600px height.
                               width=0/height=0 + sizes="100vw" is the Next.js pattern for
                               responsive images with unknown dimensions (style drives the layout). */
                            <div 
                                className='mt-3 -mx-4 cursor-pointer overflow-hidden bg-gray-100 max-h-[500px] max-w-[400px] sm:max-w-[300px]' 
                                style={{ width: 'calc(100% + 2rem)'}}
                                onClick={(event) => onOpenImage(event, buzz.media, "image/jpeg")}
                            >
                                <Image
                                    src={buzz.media}
                                    alt='image'
                                    width={0}
                                    height={0}
                                    sizes="100vw"
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                    placeholder="blur"
                                    blurDataURL={s3Static + "placeholder.png"}
                                    unoptimized={buzz.mediaType === "image/gif"}
                                />
                            </div>
                        )}
                        {buzz.mediaType.includes("video") && (
                            <div className='mt-3 relative w-full bg-gray-900 -mx-3 sm:mx-0' style={{width: 'calc(100% + 1.5rem)'}}>                                  
                                <ReactPlayer 
                                    src={buzz.media} 
                                    loop={true}
                                    muted={buzz.entityId != viewEntityId || isMuteVideo}
                                    playing={buzz.entityId === viewEntityId && !isPauseVideo}
                                    height="auto"
                                    width="100%"
                                    playsInline={true}
                                    onError={(e) => {
                                        console.error("Video play error:", e)
                                    }}
                                    className='sm:max-h-[500px]'
                                />
                                <div className='absolute flex items-center justify-center right-0 left-0 top-0 bottom-0 m-auto text-white text-6xl'>
                                    {isPauseVideo ?
                                        <button className='w-full h-full flex justify-center items-center bg-black/20' onClick={() => setIsPauseVideo(false)}>
                                            <FaPlayCircle/>
                                        </button>
                                    :
                                        <button className='w-full h-full opacity-0' onClick={() => setIsPauseVideo(true)}>
                                            <FaPause/>
                                        </button>
                                    }
                                </div> 
                                <div className='absolute right-0 top-0 bottom-0 px-2 m-auto flex text-white text-2xl'>
                                    {isMuteVideo ?
                                        <button className='p-2' onClick={() => setIsMuteVideo(false)}>
                                            <FaVolumeMute/>
                                        </button>
                                    :
                                        <button className='p-2' onClick={() => setIsMuteVideo(true)}>
                                            <FaVolumeUp/>
                                        </button>
                                    }
                                </div>                                            
                            </div>
                        )} 
                    </>
                )}

                {/* Action Buttons */}
                <div className='flex items-center justify-between pt-3 mt-3 border-t border-gray-100'>
                    <div className='flex items-center gap-4'>
                        <motion.button 
                            whileTap={{ scale: 0.95 }}
                            className='flex items-center gap-1.5 text-gray-600 hover:text-orange-600 transition-colors text-sm'
                            onClick={() => onOpenBuzz(buzz.entityId)}
                        >
                            <MessageCircle size={18} />
                            <span className='font-medium'>{buzz.commentCount}</span>
                        </motion.button>
                        <LikeButton
                            isLiked={buzz.like === 'primary'}
                            onLike={onLike}
                            onUnlike={onUnlike}
                            likeCount={buzz.likeCount}
                        />
                    </div>
                    <motion.button 
                        whileTap={{ scale: 0.95 }}
                        className='p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-all'
                        onClick={onShare}
                    >
                        <Share2 size={18} />
                    </motion.button>
                </div>

                {/* Comment Preview */}
                {buzz.content && (
                    <div className='text-sm md:text-base py-2 border-t border-gray-100 mt-2 pt-2'>
                        <Link href={`/profile/${buzz.actHandleName}`} className='font-medium text-orange-600 hover:text-orange-700'>
                            {buzz.actUserNm}
                        </Link>
                        <span className='text-gray-600'>
                            &nbsp;{buzz.content}
                        </span>
                    </div>  
                )}           
            </motion.div>
        </motion.div>
    )
}
